import type { ReactNode } from 'react'
import { differenceInYears, parseISO } from 'date-fns'
import { HugeiconsIcon } from '@hugeicons/react'
import { Building03Icon, Camera01Icon } from '@hugeicons/core-free-icons'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { cn, toTitleCase } from '@/lib/utils'

export interface PersonIdentityData {
  firstName?: string
  lastName?: string
  name?: string
  gender?: 'male' | 'female' | null
  dateOfBirth?: string | null
  kind?: 'person' | 'company'
  photoUrl?: string | null
}

export function personAge(dateOfBirth?: string | null): number | null {
  if (!dateOfBirth) return null
  const birthday = parseISO(dateOfBirth.slice(0, 10))
  const now = new Date()
  if (birthday > now) return null
  const age = differenceInYears(now, birthday)
  return Number.isFinite(age) && age >= 0 ? age : null
}

export function personGenderAge(person: PersonIdentityData): string {
  if (person.kind === 'company') return 'Empresa'
  const age = personAge(person.dateOfBirth)
  return [
    person.gender === 'male' ? 'Masculino' : person.gender === 'female' ? 'Femenino' : 'Sexo sin registrar',
    age === null ? 'Edad sin registrar' : `${age} años`,
  ].join(' · ')
}

export function personFullName(person: PersonIdentityData): string {
  if (person.name) return person.name
  return toTitleCase(person.kind === 'company'
    ? `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim()
    : [person.lastName, person.firstName].filter(Boolean).join(', '))
}

export function PersonAvatar({ person, className }: { person: PersonIdentityData; className?: string }) {
  const initials = person.lastName || person.firstName
    ? `${person.lastName?.charAt(0) ?? ''}${person.firstName?.charAt(0) ?? ''}`
    : (person.name ?? '').split(/\s+/).filter(Boolean).slice(0, 2).map(word => word.charAt(0)).join('')
  return (
    <Avatar className={cn('size-8 shrink-0', className)}>
      {person.photoUrl ? <AvatarImage src={person.photoUrl} alt="" /> : null}
      <AvatarFallback className="text-[10px]">
        {person.kind === 'company' ? <HugeiconsIcon icon={Building03Icon} className="size-4" aria-hidden="true" /> : initials.toUpperCase() || '?'}
      </AvatarFallback>
    </Avatar>
  )
}

export function PersonIdentity({ person, className, avatarClassName, detail, onEditPhoto }: {
  person: PersonIdentityData
  className?: string
  avatarClassName?: string
  detail?: ReactNode
  onEditPhoto?: () => void
}) {
  return (
    <span className={cn('flex min-w-0 items-center gap-3 text-left', className)}>
      {onEditPhoto ? (
        <Button
          type="button"
          variant="ghost"
          className="group/photo relative h-auto rounded-full p-0"
          onClick={onEditPhoto}
          aria-label={`Editar foto de ${personFullName(person)}`}
          title="Editar foto"
        >
          <PersonAvatar person={person} className={avatarClassName} />
          <span className="absolute inset-0 flex items-center justify-center rounded-full bg-foreground/70 text-background opacity-0 transition-opacity group-hover/photo:opacity-100 group-focus-visible/photo:opacity-100 motion-reduce:transition-none [@media(hover:none)]:opacity-100">
            <HugeiconsIcon icon={Camera01Icon} aria-hidden="true" />
          </span>
        </Button>
      ) : <PersonAvatar person={person} className={avatarClassName} />}
      <span className="flex min-w-0 flex-col whitespace-normal">
        <span className="break-words font-medium text-foreground/90">{personFullName(person)}</span>
        <span className="text-xs font-normal text-muted-foreground">{personGenderAge(person)}</span>
        {detail && <span className="text-xs font-normal text-muted-foreground">{detail}</span>}
      </span>
    </span>
  )
}
